import {
  BusinessStatus,
  OnlinePaymentStatus,
  Shift4OnboardingStatus,
} from "../../../interfaces/businessFull.interface";

// One label serves every acquirer: "the acquirer is reviewing them" is the same
// state whether it arrives as Rapyd's `pendingVerification`, Adyen's
// `verificationPending` or Shift4's `underwriting`.
export const ACQUIRER_REVIEW_LABEL = "Acquirer Review";

/** One label for the never-submitted state, so cell, dots and tests cannot drift. */
export const NEVER_SUBMITTED_LABEL = "Pending Submission";

export const mapStatusLabels = (status: BusinessStatus) => {
  switch (status) {
    case "pendingSubmission":
      return { label: "Pending Submission", color: "yellow" };
    // Pre-submission like pendingSubmission; without a case it rendered an
    // empty tag, and the acquirer filter can now select exactly this population.
    case "pendingOutreach":
      return { label: "Pending Outreach", color: "yellow" };
    case "pendingVerification":
      return { label: ACQUIRER_REVIEW_LABEL, color: "blue" };
    case "verified":
      return { label: "Approved", color: "green" };
    case "unverified":
      return { label: "Declined", color: "red" };
    case "inactivated":
      return { label: "Inactivated", color: "gray" };
    default:
      return { label: "", color: "white" };
  }
};

export const mapOnlinePaymentStatusLabels = (
  status?: OnlinePaymentStatus | string
): { label: string; color: string } | null => {
  switch (status) {
    case "onboardingAllowed":
      return { label: "Pending Submission", color: "default" };
    case "onboardingDisallowed":
      return { label: "Onboarding Disallowed", color: "red" };
    case "initiated":
      return { label: "Submitted", color: "blue" };
    // Same operator-facing label: the merchant still owes Adyen input.
    case "awaitingMerchantAction":
    case "awaitingData":
      return { label: "Awaiting Data", color: "orange" };
    case "pendingToS":
      return { label: "Pending Terms of Service", color: "orange" };
    case "pending":
      return { label: "Pending", color: "orange" };
    case "verificationPending":
      return { label: ACQUIRER_REVIEW_LABEL, color: "blue" };
    case "verified":
      return { label: "Approved", color: "green" };
    case "rejected":
      return { label: "Declined", color: "red" };
    default:
      return null;
  }
};

/**
 * Absent state is meaningful here, unlike the other two: no `shift4` sub-document
 * means the merchant was never submitted, which is what the Submit action acts on.
 * An unrecognised status is not that state — it keeps its raw value.
 */
export const mapShift4StatusLabels = (
  status?: Shift4OnboardingStatus | string
): { label: string; color: string } => {
  if (!status) {
    return { label: NEVER_SUBMITTED_LABEL, color: "default" };
  }
  switch (status) {
    case "submitted":
      return { label: "Submitted", color: "blue" };
    case "created":
      return { label: "Created", color: "blue" };
    case "underwriting":
      return { label: ACQUIRER_REVIEW_LABEL, color: "blue" };
    case "approved":
      return { label: "Approved", color: "green" };
    case "failed":
      return { label: "Failed", color: "red" };
    case "rejected":
      return { label: "Declined", color: "red" };
    default:
      // Not the never-submitted state: Submit keys off the raw status, so a
      // status this mapper does not know must not read as "never sent".
      return { label: status, color: "gray" };
  }
};
